import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, FormControl, InputLabel, Select, MenuItem, Alert, Checkbox, CircularProgress, Stack } from '@mui/material';
import { Add as AddIcon, ListAltOutlined as EditIcon, DeleteOutlined as DeleteIcon, ExpandMore as ExpandMoreIcon, FilterListOutlined as FilterIcon } from '@mui/icons-material';
import PolicyModal from '../components/PolicyModal';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { UserAPI } from '../apis/UserAPI';
import { useAllApplicationsList, usePoliciesList, clearPoliciesDataCache } from '../components/ApplicationContext';
import { isAuthorized } from '../utils/utilities';
import authenticatedRoute from '../views/AuthenticatedRoute';
import { constants } from '../constants';
import { useThemeDetails } from '../components/ThemeDetailsContext';
// Helper function for bulk delete processing
const processPolicyDeletion = async (policyId, policies) => {
    const policyToDelete = policies.find((p) => p.displayId === policyId);
    if (!policyToDelete)
        return false;
    // Clean up role-policy mappings
    const rolePolicyRecords = await UserAPI.getPolicyRoleRecords(policyToDelete.id.toString());
    for (const record of rolePolicyRecords) {
        await UserAPI.deleteRolePolicyById(record.rolepolicy_id);
    }
    // Delete the policy
    await UserAPI.deletePolicy(policyToDelete.id.toString());
    return true;
};
// Helper function to create bulk delete result message
const createBulkDeleteMessage = (successCount, errorCount) => {
    const policyText = successCount === 1
        ? constants.policy_mgmt_word_policy
        : constants.policy_mgmt_word_policies;
    const successMessage = `${successCount} ${policyText} ${constants.policy_mgmt_phrase_deleted_successfully}`;
    if (errorCount > 0) {
        return `${successMessage}. ${errorCount} ${constants.policy_mgmt_phrase_failed_to_delete_suffix}`;
    }
    return successMessage;
};
// Helper function to render policy specification
const renderPolicySpecification = (policy, expandedSpecs, toggleSpecificationExpansion) => {
    const displayId = policy.displayId;
    const isExpanded = displayId ? expandedSpecs[displayId] || false : false;
    const spec = policy.specification || constants.policy_mgmt_no_spec;
    return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 1 }, children: [_jsx(Box, { component: "pre", sx: {
                    fontFamily: 'Consolas, Monaco, monospace',
                    fontSize: '13px',
                    lineHeight: '20px',
                    color: '#212121',
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    margin: 0,
                    overflow: 'hidden',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: isExpanded ? 6 : 2,
                    WebkitBoxOrient: 'vertical',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    minHeight: isExpanded ? '120px' : '40px',
                    ...(isExpanded && {
                        overflowY: 'auto',
                        maxHeight: '120px'
                    }),
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '3px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '3px',
                        '&:hover': { backgroundColor: '#a8a8a8' }
                    }
                }, children: spec }), _jsx(IconButton, { onClick: () => displayId && toggleSpecificationExpansion(displayId), sx: {
                    padding: '6px',
                    color: '#595959',
                    alignSelf: 'center',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: '#26890d'
                    }
                }, children: _jsx(ExpandMoreIcon, { sx: {
                        fontSize: '20px',
                        transform: isExpanded
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    } }) })] }));
};
const PolicyManagement = () => {
    const { stateImages } = useThemeDetails();
    const [policies, setPolicies] = useState([]);
    const [applications, setApplications] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState('');
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState(null);
    // Modal and dialog state
    const [policyModalOpen, setPolicyModalOpen] = useState(false);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [policyToDelete, setPolicyToDelete] = useState(null);
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [expandedSpecs, setExpandedSpecs] = useState({});
    const [selectedPolicies, setSelectedPolicies] = useState([]);
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    // Load data on component mount
    useEffect(() => {
        loadInitialData();
    }, []);
    const getAllApplications = useAllApplicationsList();
    const getPolicies = usePoliciesList();
    const loadInitialData = async () => {
        setDataLoading(true);
        setError(null);
        try {
            // Load applications and policies
            const apps = await getAllApplications();
            setApplications(apps);
            const policiesResponse = await getPolicies();
            // Transform API policies to component format
            const transformedPolicies = policiesResponse.map((policy) => ({
                ...policy,
                displayId: policy.id.toString(), // Use id as displayId for UI purposes
                specification: JSON.stringify({
                    resourceType: policy.resourceType,
                    id: policy.id,
                    application_id: policy.application_id,
                    name: policy.name,
                    effect: policy.effect,
                    resource_action: policy.resource_action
                }, null, 2),
                dateAdded: policy.metadata?.createdDate
                    ? new Date(policy.metadata.createdDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    })
                    : 'Unknown'
            }));
            setPolicies(transformedPolicies);
            // Set default application filter to first application if available
            if (apps.length > 0 && !selectedApplication) {
                setSelectedApplication(apps[0].name);
            }
        }
        catch (err) {
            console.error('Failed to load data:', err);
            setError(constants.policy_mgmt_error_load_policies);
        }
        finally {
            setDataLoading(false);
        }
    };
    const handleAddPolicy = () => {
        setSelectedPolicy(null);
        setPolicyModalOpen(true);
    };
    const handleEditPolicy = (policy) => {
        setSelectedPolicy(policy);
        setPolicyModalOpen(true);
    };
    const handleDeletePolicy = (policy) => {
        setPolicyToDelete(policy);
        setDeleteDialogOpen(true);
    };
    // Convert PolicyWithId to Policy for modal compatibility
    const convertToModalPolicy = (policy) => {
        if (!policy)
            return null;
        return {
            id: policy.displayId,
            name: policy.name,
            resourceType: policy.resourceType,
            effect: policy.effect,
            resource_action: policy.resource_action,
            specification: policy.specification ?? '',
            dateAdded: policy.dateAdded,
            application_id: policy.application_id
        };
    };
    const handleSavePolicy = async (policyData) => {
        setLoading(true);
        setError(null);
        try {
            if (selectedPolicy) {
                // Update existing policy
                // Use application_id from policyData if provided (from JSON edit), otherwise use existing
                const applicationIdToUse = policyData.application_id ?? selectedPolicy.application_id;
                await UserAPI.updatePolicy(selectedPolicy.id.toString(), {
                    organization_id: selectedPolicy.organization_id,
                    application_id: applicationIdToUse,
                    name: policyData.name,
                    effect: policyData.effect,
                    resource_action: policyData.resource_action
                });
                // Clear cache so loadInitialData fetches fresh data (not stale cached)
                clearPoliciesDataCache();
                // Refresh data from server to get the latest state
                await loadInitialData();
                setSuccessMessage(constants.policy_mgmt_update_success);
            }
            else {
                // For new policy, application_id comes from the modal's dropdown selection
                // Empty array is valid (means 'None' / no application)
                const applicationId = policyData.application_id ?? [];
                // Get organization_id from the first selected application, or use first available app's org_id for 'None'
                const firstAppId = applicationId[0];
                const firstApp = firstAppId
                    ? applications.find((app) => String(app.id) === String(firstAppId))
                    : null;
                const organizationId = firstApp?.organization_id ??
                    applications[0]?.organization_id;
                if (!organizationId) {
                    setError('No organization available. Please ensure at least one application exists.');
                    return;
                }
                // Add new policy
                const createRequest = {
                    organization_id: organizationId,
                    application_id: applicationId,
                    name: policyData.name,
                    effect: policyData.effect,
                    resource_action: policyData.resource_action
                };
                await UserAPI.createPolicy(createRequest);
                clearPoliciesDataCache();
                // Refresh data from server to get the latest state
                await loadInitialData();
                setSuccessMessage(constants.policy_mgmt_add_success);
            }
            setPolicyModalOpen(false);
            setSelectedPolicy(null);
        }
        catch (err) {
            console.error('Error saving policy:', err);
            setError(constants.policy_mgmt_error_save_policy);
        }
        finally {
            setLoading(false);
        }
    };
    const confirmDeletePolicy = async () => {
        if (policyToDelete) {
            setLoading(true);
            setError(null);
            try {
                // First, clean up any role-policy mappings for this policy
                const rolePolicyRecords = await UserAPI.getPolicyRoleRecords(policyToDelete.id.toString());
                // Delete all role-policy mappings for this policy
                if (rolePolicyRecords.length > 0) {
                    console.log(`Cleaning up ${rolePolicyRecords.length} role-policy mappings for policy ${policyToDelete.name}`);
                    for (const record of rolePolicyRecords) {
                        await UserAPI.deleteRolePolicyById(record.rolepolicy_id);
                    }
                }
                // Then delete the policy itself
                await UserAPI.deletePolicy(policyToDelete.id.toString());
                setPolicies((prevPolicies) => prevPolicies.filter((p) => p.id !== policyToDelete.id));
                // Clear the deleted policy from selection if it was selected
                setSelectedPolicies((prev) => prev.filter((id) => id !== policyToDelete.displayId));
                setSelectAllChecked(false);
                clearPoliciesDataCache();
                setSuccessMessage(constants.policy_mgmt_delete_success);
                setDeleteDialogOpen(false);
                setPolicyToDelete(null);
            }
            catch (err) {
                console.error('Error deleting policy:', err);
                setError(constants.policy_mgmt_error_delete_policy);
            }
            finally {
                setLoading(false);
            }
        }
    };
    const handleBulkDelete = () => {
        setBulkDeleteDialogOpen(true);
    };
    const confirmBulkDelete = async () => {
        if (selectedPolicies.length === 0)
            return;
        setLoading(true);
        setError(null);
        let successCount = 0;
        let errorCount = 0;
        try {
            // Process each selected policy
            for (const policyId of selectedPolicies) {
                try {
                    const success = await processPolicyDeletion(policyId, policies);
                    if (success) {
                        successCount++;
                    }
                }
                catch (err) {
                    console.error(`Error deleting policy ${policyId}:`, err);
                    errorCount++;
                }
            }
            // Update local state - remove successfully deleted policies
            setPolicies((prevPolicies) => prevPolicies.filter((p) => p.displayId && !selectedPolicies.includes(p.displayId)));
            // Clear selections
            setSelectedPolicies([]);
            setSelectAllChecked(false);
            // Set appropriate messages
            if (successCount > 0) {
                setSuccessMessage(createBulkDeleteMessage(successCount, errorCount));
            }
            if (errorCount > 0 && successCount === 0) {
                setError(constants.policy_mgmt_error_bulk_delete);
            }
            setBulkDeleteDialogOpen(false);
        }
        catch (err) {
            console.error('Error during bulk delete:', err);
            setError(constants.policy_mgmt_error_bulk_delete);
        }
        finally {
            setLoading(false);
        }
    };
    const toggleSpecificationExpansion = (policyId) => {
        setExpandedSpecs((prev) => ({
            ...prev,
            [policyId]: !prev[policyId]
        }));
    };
    const handleSelectAll = (checked) => {
        setSelectAllChecked(checked);
        const updatedSelectedPolicies = checked
            ? policies
                .map((p) => p.displayId)
                .filter((id) => id !== undefined)
            : [];
        setSelectedPolicies(updatedSelectedPolicies);
    };
    const handleSelectPolicy = (policyId, checked) => {
        const updatedSelectedPolicies = checked
            ? [...selectedPolicies, policyId]
            : selectedPolicies.filter((id) => id !== policyId);
        setSelectedPolicies(updatedSelectedPolicies);
        if (!checked) {
            setSelectAllChecked(false);
        }
    };
    // Check view permission
    if (!isAuthorized('RBAC:ViewPolicy')) {
        return (_jsx(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: _jsxs(Box, { sx: { textAlign: 'center', py: 8 }, children: [_jsx(Typography, { variant: "h6", color: "#666", gutterBottom: true, children: constants.policy_mgmt_access_denied_title }), _jsx(Typography, { variant: "body1", color: "#888", children: constants.policy_mgmt_access_denied_desc })] }) }));
    }
    return (_jsxs(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: [_jsxs(Paper, { sx: { p: 3, borderRadius: 2, boxShadow: 'none' }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3
                        }, children: [_jsx(Typography, { variant: "body1", sx: { mb: 3 }, children: constants.policy_mgmt_header_description }), _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [selectedPolicies.length > 0 &&
                                        isAuthorized('RBAC:DeletePolicy') && (_jsx(Button, { variant: "outlined", onClick: handleBulkDelete, sx: {
                                            color: '#DA291C',
                                            border: '1px solid #DA291C',
                                            backgroundColor: 'transparent',
                                            boxShadow: 'none',
                                            textTransform: 'none',
                                            minWidth: '44px',
                                            width: '44px',
                                            height: '44px'
                                        }, children: _jsx(DeleteIcon, {}) })), _jsxs(FormControl, { size: "small", sx: { minWidth: 200 }, children: [_jsx(InputLabel, { children: constants.policy_mgmt_application_label }), _jsxs(Select, { value: selectedApplication, onChange: (e) => setSelectedApplication(e.target.value), label: constants.policy_mgmt_application_label, disabled: dataLoading, children: [_jsx(MenuItem, { value: "none", children: constants.global_policies }), applications.map((app) => (_jsx(MenuItem, { value: app.name, children: app.name }, app.id || app.name)))] })] }), isAuthorized('RBAC:CreatePolicy') && (_jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: handleAddPolicy, sx: {
                                            textTransform: 'none',
                                            boxShadow: 'none'
                                        }, children: constants.policy_mgmt_add_button }))] })] }), dataLoading && (_jsxs(Box, { sx: { textAlign: 'center', py: 4 }, children: [stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, {})), _jsx(Typography, { variant: "body2", sx: { mt: 2 }, children: constants.policy_mgmt_loading })] })), error && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, children: error })), successMessage && (_jsx(Alert, { severity: "success", sx: { mb: 3 }, onClose: () => setSuccessMessage(''), children: successMessage })), !dataLoading && !error && (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb',
                                                    width: '60px',
                                                    px: 2
                                                }, children: _jsx(Checkbox, { checked: selectAllChecked, onChange: (e) => handleSelectAll(e.target.checked), sx: {
                                                        color: '#d9d9d9',
                                                        '&.Mui-checked': {
                                                            color: '#26890d'
                                                        }
                                                    } }) }), _jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb',
                                                    width: '270px'
                                                }, children: _jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.policy_mgmt_table_header_name }), _jsx(FilterIcon, { sx: {
                                                                fontSize: 18,
                                                                color: '#26890d'
                                                            } })] }) }), _jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb'
                                                }, children: _jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.policy_mgmt_table_header_specification }) }), _jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb',
                                                    width: '200px'
                                                }, children: _jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.policy_mgmt_table_header_actions }) })] }) }), _jsx(TableBody, { children: (() => {
                                        const filteredPolicies = policies.filter((policy) => {
                                            if (!selectedApplication)
                                                return true;
                                            // Handle "None" option - show policies without application_id
                                            if (selectedApplication === 'none') {
                                                if (!policy.application_id)
                                                    return true;
                                                if (Array.isArray(policy.application_id)) {
                                                    return policy.application_id.every((id) => id === null ||
                                                        id === undefined ||
                                                        id === 'null');
                                                }
                                                return false;
                                            }
                                            // Find the application by name to get its ID
                                            const selectedApp = applications.find((app) => app.name ===
                                                selectedApplication);
                                            if (!selectedApp)
                                                return false;
                                            // Check if the policy's application_id matches (handling both string and number types)
                                            const selectedAppIdStr = String(selectedApp.id);
                                            if (Array.isArray(policy.application_id)) {
                                                return policy.application_id.some((id) => String(id) ===
                                                    selectedAppIdStr);
                                            }
                                            else {
                                                return (String(policy.application_id) === selectedAppIdStr);
                                            }
                                        });
                                        if (filteredPolicies.length === 0) {
                                            return (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 4, sx: {
                                                        textAlign: 'center',
                                                        py: 4
                                                    }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: constants.policy_mgmt_no_policies_found }) }) }));
                                        }
                                        return filteredPolicies.map((policy) => (_jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2,
                                                        px: 2
                                                    }, children: _jsx(Checkbox, { checked: policy.displayId
                                                            ? selectedPolicies.includes(policy.displayId)
                                                            : false, onChange: (e) => {
                                                            if (policy.displayId) {
                                                                handleSelectPolicy(policy.displayId, e.target.checked);
                                                            }
                                                        }, sx: {
                                                            color: '#d9d9d9',
                                                            '&.Mui-checked': {
                                                                color: '#26890d'
                                                            }
                                                        } }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2
                                                    }, children: _jsx(Typography, { variant: "body1", sx: {
                                                            fontWeight: 600,
                                                            color: isAuthorized('RBAC:UpdatePolicy')
                                                                ? '#26890d'
                                                                : '#212121',
                                                            textDecoration: isAuthorized('RBAC:UpdatePolicy')
                                                                ? 'underline'
                                                                : 'none',
                                                            cursor: isAuthorized('RBAC:UpdatePolicy')
                                                                ? 'pointer'
                                                                : 'default',
                                                            '&:hover': isAuthorized('RBAC:UpdatePolicy')
                                                                ? {
                                                                    color: '#1f6b0a'
                                                                }
                                                                : {}
                                                        }, onClick: isAuthorized('RBAC:UpdatePolicy')
                                                            ? () => handleEditPolicy(policy)
                                                            : undefined, children: policy.name }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2,
                                                        maxWidth: '500px',
                                                        minWidth: '300px'
                                                    }, children: renderPolicySpecification(policy, expandedSpecs, toggleSpecificationExpansion) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2
                                                    }, children: _jsxs(Box, { sx: {
                                                            display: 'flex',
                                                            gap: 1
                                                        }, children: [isAuthorized('RBAC:UpdatePolicy') && (_jsx(IconButton, { onClick: () => handleEditPolicy(policy), sx: {
                                                                    color: '#26890d',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(38, 137, 13, 0.04)'
                                                                    }
                                                                }, children: _jsx(EditIcon, {}) })), isAuthorized('RBAC:DeletePolicy') && (_jsx(IconButton, { onClick: () => handleDeletePolicy(policy), sx: {
                                                                    color: '#da291c',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(218, 41, 28, 0.04)'
                                                                    }
                                                                }, children: _jsx(DeleteIcon, {}) }))] }) })] }, policy.id)));
                                    })() })] }) }))] }), _jsx(PolicyModal, { open: policyModalOpen, onClose: () => {
                    setPolicyModalOpen(false);
                    setSelectedPolicy(null);
                }, onSave: handleSavePolicy, policy: convertToModalPolicy(selectedPolicy), loading: loading, applications: applications }), _jsx(ConfirmationDialog, { isOpen: deleteDialogOpen, setIsOpen: () => {
                    setDeleteDialogOpen(false);
                    setPolicyToDelete(null);
                }, submitFunction: confirmDeletePolicy, bodyText: constants.policy_mgmt_delete_confirm_body(policyToDelete?.name ?? ''), submitButtonText: constants.delete, loading: loading }), _jsx(ConfirmationDialog, { isOpen: bulkDeleteDialogOpen, setIsOpen: () => {
                    setBulkDeleteDialogOpen(false);
                }, submitFunction: confirmBulkDelete, bodyText: constants.policy_mgmt_bulk_delete_confirm_body(selectedPolicies.length), submitButtonText: constants.delete, loading: loading })] }));
};
export default authenticatedRoute(PolicyManagement);
